import { createAction } from 'typesafe-actions';

import {
    AiChatDataCollectionOption,
    AiChatDataExplorerState,
    AiChatFeedbackInput,
    AiChatFeedbackReaction,
    ChartType,
    DataFilters,
    DataQueryParams,
    DataQueryType,
    DataResponse,
    DataType,
} from '@portal/common/types';

import { AiChatActivePayload, AiChatDataCollectionPickerPayload } from './types';

export interface QueryResultRequest {
    id: string;
    queryType: DataQueryType;
    dataType: DataType;
    filters: DataFilters;
    params: DataQueryParams;
    dataCollectionId: number | null;
}

export const submitChatMessage = createAction('CHAT_MESSAGE_SUBMITTED')<{
    id: string;
    content: string;
}>();
export const cancelChatMessage = createAction('CHAT_MESSAGE_CANCELLED')();
export const addAssistantMessage = createAction('CHAT_ASSISTANT_MESSAGE_ADDED')<{
    id: string;
    content: string;
    dataCollectionId: number | null;
}>();
export const addAssistantFilters = createAction('CHAT_ASSISTANT_FILTERS_ADDED')<{
    id: string;
    dataTypes: DataType[];
    filters: DataFilters;
    visualization?: ChartType;
    dataCollectionId: number | null;
}>();
export const addAssistantDataCollectionPicker = createAction(
    'CHAT_ASSISTANT_DATA_COLLECTION_PICKER_ADDED'
)<AiChatDataCollectionPickerPayload>();
export const pickChatDataCollection = createAction('CHAT_DATA_COLLECTION_PICKED')<{
    messageId: string;
    dataCollectionId: number;
}>();
export const saveChatDataCollectionChoice = createAction('CHAT_DATA_COLLECTION_CHOICE_SAVED')<{
    responseId: string;
    dataCollectionId: number;
}>();
export const dismissChatDataCollectionPicker = createAction(
    'CHAT_DATA_COLLECTION_PICKER_DISMISSED'
)<string>();
export const switchChatDataCollection = createAction('CHAT_DATA_COLLECTION_SWITCH_REQUESTED')<{
    responseId: string;
    dataCollectionId: number;
}>();
export const chatDataCollectionSwitchApplied = createAction('CHAT_DATA_COLLECTION_SWITCH_APPLIED')<{
    responseId: string;
    dataCollectionId: number;
    dataTypes: DataType[];
    filters: DataFilters;
}>();
export const addAssistantQueryResult = createAction('CHAT_ASSISTANT_QUERY_RESULT_ADDED')<
    Omit<QueryResultRequest, 'dataType'> & {
        dataTypes: DataType[];
        visualization?: ChartType;
        dataCollectionOptions: AiChatDataCollectionOption[];
    }
>();
export const fetchQueryResult = createAction(
    'CHAT_QUERY_RESULT_FETCH_REQUESTED'
)<QueryResultRequest>();
export const queryResultLoaded = createAction('CHAT_QUERY_RESULT_LOADED')<{
    id: string;
    data: DataResponse;
}>();
export const queryResultFailed = createAction('CHAT_QUERY_RESULT_FAILED')<{
    id: string;
    error: string;
}>();
export const setMessageReaction = createAction('CHAT_MESSAGE_REACTION_SET')<{
    id: string;
    reaction: AiChatFeedbackReaction | null;
}>();
export const submitFeedback = createAction('CHAT_FEEDBACK_SUBMITTED')<
    { responseId: string } & AiChatFeedbackInput
>();
export const submitFeedbackSucceeded = createAction('CHAT_FEEDBACK_SUBMIT_SUCCEEDED')<{
    responseId: string;
    reaction: AiChatFeedbackReaction;
}>();
export const submitFeedbackFailed = createAction('CHAT_FEEDBACK_SUBMIT_FAILED')<{
    errorMessage: string;
}>();
export const resetFeedbackSubmission = createAction('CHAT_FEEDBACK_SUBMISSION_RESET')();
export const chatRequestFailed = createAction('CHAT_REQUEST_FAILED')<{
    id: string;
    content: string;
    failedInput: string | null;
}>();
export const setChatId = createAction('CHAT_ID_SET')<string>();
export const setChatAutoDataCollection = createAction('CHAT_AUTO_DATA_COLLECTION_SET')<boolean>();
export const setManualFiltersSyncEnabled = createAction(
    'CHAT_MANUAL_FILTERS_SYNC_ENABLED_SET'
)<boolean>();
export const manualFiltersPosted = createAction('CHAT_MANUAL_FILTERS_POSTED')<{
    id: string;
    promptId: string;
    manualFilters: AiChatDataExplorerState;
}>();
export const resetChat = createAction('CHAT_RESET')();

export const setActiveChat = createAction('CHAT_ACTIVE_SET')<AiChatActivePayload>();
